/**
 * The line at the foot of the teacher's overview.
 *
 * A rotating list rather than the one Mandela quote that used to be hard-coded
 * into the page: a "tip of the day" that never changes is a banner people stop
 * reading by the second morning.
 *
 * Deliberately not a table and not an API call. Nothing here is a fact about an
 * institute, nobody edits it, and every school gets the same list — so it is
 * shipped with the build like any other piece of copy. Should an institute ever
 * want to write its own, that is the notice board, which already exists.
 *
 * Kept general on purpose (CLAUDE.md §4): nothing here names a board, a
 * subject, an age group or a country's curriculum.
 */

export interface TeachingTip {
  text: string;
  /** Who said it, or null when it is not a quotation. */
  source: string | null;
}

export const TEACHING_TIPS: readonly TeachingTip[] = [
  {
    text: 'Education is the most powerful weapon which you can use to change the world.',
    source: 'Nelson Mandela',
  },
  {
    text: 'A child who has not been marked absent for a week is worth a word of praise — attendance is a habit, and habits are noticed.',
    source: null,
  },
  {
    text: 'Tell me and I forget, teach me and I may remember, involve me and I learn.',
    source: 'Benjamin Franklin',
  },
  {
    text: 'Write up the lesson while it is still in your head. The version you remember tonight is thinner than the one you remember now.',
    source: null,
  },
  {
    text: 'The best teachers are those who show you where to look but do not tell you what to see.',
    source: 'Alexandra K. Trenfor',
  },
  {
    text: 'Ask the quiet child first. The hands that go up were going to answer anyway.',
    source: null,
  },
  {
    text: 'Children must be taught how to think, not what to think.',
    source: 'Margaret Mead',
  },
  {
    text: 'A mark returned within the week is feedback. A mark returned a month later is a verdict.',
    source: null,
  },
  {
    text: 'It is the supreme art of the teacher to awaken joy in creative expression and knowledge.',
    source: 'Albert Einstein',
  },
  {
    text: 'When a whole class gets the same question wrong, the question is worth rereading before the class is.',
    source: null,
  },
];

/**
 * The tip for one day, `YYYY-MM-DD`.
 *
 * Chosen by the date rather than at random, so it does not change under the
 * reader when the screen refetches, and so every teacher in an institute is
 * looking at the same line when one mentions it to another.
 */
export function tipForDay(day: string): TeachingTip {
  // Days since the epoch. Parsed as UTC because the string is a calendar day —
  // read as a local instant it would step a day early west of Greenwich.
  const days = Math.floor(Date.parse(`${day}T00:00:00.000Z`) / 86_400_000);
  if (!Number.isFinite(days)) return TEACHING_TIPS[0];

  return TEACHING_TIPS[((days % TEACHING_TIPS.length) + TEACHING_TIPS.length) % TEACHING_TIPS.length];
}
